import { Component, Element, h, Host, Listen, Prop, Watch } from "@stencil/core";

import { StzhRadioChangeEvent } from "../../index";

import { hasSlot } from "../../utils/utils";
import { camelCase } from "../../utils/string-utils";
import { StzhLocaleComponent } from "../../index";

import { StzhInputDescription } from "../stzh-input/stzh-input-description";

const SELECTOR_RADIO = "stzh-radio, stzh-button[type=radio], stzh-card";

let radiogroupCounter = 0;

/**
 * @slot - Slot for `stzh-radio`, `stzh-button[type="radio"]` or `stzh-card` elements
 * @slot description - Slot for description
 * @slot description-long - Slot for long description (in popover)
 * @slot description-long-title - Slot for long description title (in popover) / instead of descriptionLongTitle or description property
 * @slot error - Slot for error
 */
@Component({
  tag: "stzh-radiogroup",
  styleUrl: "stzh-radiogroup.scss",
  scoped: true,
})
export class StzhRadiogroup {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** The name of the input radio elements, will define the radio group. If you have more than one radio group on a page, every group needs to have its own unique name */
  @Prop({ reflect: true }) name: string;

  /** The legend */
  @Prop() legend: string;

  /** Hide legend to show only for screenreaders */
  @Prop({ reflect: true }) hideLegend: boolean = false;

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Hide `(optional)` label (or use `required` inside form to hide it) */
  @Prop({ reflect: true }) hideOptional: boolean = false;

  /** Select a radio by value */
  @Prop({ mutable: true }) value: string = "";

  /** Default value (used by reset) */
  @Prop({ mutable: true }) defaultValue: string = "";

  /** Direction */
  @Prop({ reflect: true }) direction: "vertical" | "horizontal" = "vertical";

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /**
  * **Do not use this property. It will be deprecated with the next major release.**
  * Long description title appearing in a popover (use description-long-title slot as or description property alternative) */
  @Prop() descriptionLongTitle: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /** Current checked radio element (readonly) */
  @Prop() checkedRadio: HTMLStzhRadioElement | null = null;

  /** Prevent updating certain properties of radio elements (possible values inside array: disabled, invalid, required, hide-marker) */
  @Prop() preventUpdateProperties: string[] | string = [];
  private _preventUpdateProperties: string[] = [];

  /** Host element */
  @Element() element: HTMLElement;

  @Watch("name")
  watchName(newValue: string) {
    this.updateRadiosProperty("name", newValue);
  }

  @Watch("disabled")
  watchDisabled(newValue: boolean) {
    this.updateRadiosProperty("disabled", newValue);
  }

  @Watch("required")
  watchRequired(newValue: boolean) {
    this.updateRadiosProperty("required", newValue);
  }

  @Watch("value")
  watchValue(newValue: string) {
    this.updateCheckedRadioByValue(newValue);
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  @Watch("preventUpdateProperties")
  preventUpdatePropertiesWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      this._preventUpdateProperties = JSON.parse(newValue);
    } else {
      this._preventUpdateProperties = newValue;
    }
  }

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Listen("stzhChange")
  onChange(event: CustomEvent<StzhRadioChangeEvent>) {
    this.value = event.detail.value;
  }

  /** Radio elements */
  private radios: HTMLStzhRadioElement[];
  private radiogroupId: string;
  private observer: MutationObserver;

  private updateRadiosProperty(prop: string, value: any) {
    this.radios.forEach((radio: HTMLStzhRadioElement) => {
      if (prop === "disabled") {
        // Only set the disabled if the radio is not already disabled
        if (!radio.disabled) {
          radio.disabled = value;
        }
      } else if (prop === "name" || this._preventUpdateProperties.indexOf(prop) === -1) {
        radio[camelCase(prop)] = value;
      }
    });
  }

  private get errorUsed(): boolean {
    return hasSlot(this.element, "error") || this._error?.length > 0;
  }

  private updateCheckedRadioByValue(value: string) {
    this.checkedRadio = this.radios.find(radio => radio.value === value);

    if (this.checkedRadio) {
      this.checkedRadio.checked = true;
    }

    this.uncheckRadios();
  }

  private uncheckRadios() {
    // make sure that no other radios are checked than the current one or no one
    this.radios.forEach((radio: HTMLStzhRadioElement) => {
      if (!this.checkedRadio || radio !== this.checkedRadio) {
        radio.checked = false;
      }
    });
  }

  private handleReset = async () => {
    this.value = this.defaultValue;
  };

  private init = () => {
    // update radios
    this.radios = Array.from(this.element.querySelectorAll(SELECTOR_RADIO));

    this.updateRadiosProperty("hide-optional", true);
    this.updateRadiosProperty("a11y-describedby", `${this.radiogroupId}-description`);
    this.updateRadiosProperty("invalid", this.invalid || this.errorUsed);

    this.watchValue(this.value);
    this.watchName(this.name);
    this.watchDisabled(this.disabled);
    this.watchRequired(this.required);
  };

  componentDidLoad() {
    this.defaultValue = this.defaultValue ? this.defaultValue : this.value;
  }

  async componentWillLoad() {
    this.radiogroupId = `stzh-radiogroup-${radiogroupCounter++}`;
    this.errorWatcher(this.error);
    this.preventUpdatePropertiesWatcher(this.preventUpdateProperties);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "radiogroup");
    }
  }

  componentShouldUpdate(_newValue, _oldValue, prop: string) {
    return prop !== "checkedRadio";
  }

  componentDidRender() {
    this.init();
  }

  connectedCallback() {
    if (!this.value) {
      this.radios = Array.from(this.element.querySelectorAll(SELECTOR_RADIO));

      // try to find a checkbox that might has checked set to true
      const checkedRadio = this.radios.find(radio => radio.checked);

      if (checkedRadio) {
        this.value = checkedRadio.value;
      }
    }

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const descriptionUsed = hasSlot(this.element, "description") || !!this.description;
    const descriptionLongUsed = hasSlot(this.element, "description-long") || !!this.descriptionLong;

    const classes = {
      "stzh-radiogroup": true,
      "stzh-radiogroup--has-description": descriptionUsed,
      "stzh-radiogroup--has-description-long": descriptionLongUsed,
      "stzh-radiogroup--has-error": this.errorUsed,
      "stzh-radiogroup--is-required": this.required,
      "stzh-radiogroup--is-disabled": this.disabled,
      "stzh-radiogroup--is-invalid": this.invalid || this.errorUsed,
      "stzh-radiogroup--hide-legend": this.hideLegend,
      [`stzh-radiogroup--direction-${this.direction}`]: !!this.direction,
    };

    return (
      <Host is-invalid={this.invalid || this.errorUsed}>
        <fieldset class={classes}>
          {this.legend && (
            <legend class="stzh-radiogroup__legend">
              {this.legend}
              {!this.hideOptional && (
                <span class="stzh-radiogroup__marker">
                  <span class="stzh-radiogroup__marker-symbol" aria-hidden="true">
                    {this.required
                      ? this.localization.$globals.requiredFieldMarker
                      : this.localization.$globals.optionalFieldMarker}
                  </span>
                  <span class="stzh-radiogroup__marker-text">
                    {this.required
                      ? this.localization.$globals.requiredFieldText
                      : this.localization.$globals.optionalFieldText}
                  </span>
                </span>
              )}
            </legend>
          )}
          <div class="stzh-radiogroup__fields">
            <slot></slot>
          </div>
          {(this._error?.length > 0 || this.description) && (
            <StzhInputDescription
              classPrefix="stzh-radiogroup"
              id={`${this.radiogroupId}-description`}
              error={this._error}
              description={this.description}
              descriptionLong={this.descriptionLong}
              descriptionLongTitle={this.descriptionLongTitle}
              descriptionLongUsed={descriptionLongUsed}
              moreInfoButtonLabel={this.localization.$globals.moreInfoButtonLabel}
            />
          )}
        </fieldset>
      </Host>
    );
  }
}
